/* ---------------------------------------------------------------------------
   pm-cart-shipping.js — 2026-08-24

   Deux rôles, tous deux au service de la ligne "Shipping" du cart drawer :

   1. CONVERSION DU SEUIL. La remise "livraison offerte" a un minimum de 99 USD.
      Shopify le convertit par marché, et Liquid n'a pas accès au taux de change ;
      seul le global JS Shopify.currency.rate l'expose. On calcule donc ici le
      seuil local et on ARRONDIT À L'UNITÉ SUPÉRIEURE.

      L'arrondi supérieur est une garantie, pas une coquetterie. Mesuré au
      checkout UK : £73.80 paie la livraison, £74.85 l'obtient gratuite. Le seuil
      réel est dans cette fenêtre (99 × 0.74762124 = £74.01) sans qu'on sache où.
      Annoncer £75 le place au-dessus de toute la fenêtre : la promesse tient quelle
      que soit la valeur exacte. Arrondir vers le bas promettrait au contraire une
      gratuité que le checkout refuserait — précisément la trahison qu'on corrige.

   2. AUTO-CONTRÔLE DE LA TABLE. cart.ajax.liquid porte une table de tarifs figée,
      recopiée du profil Shopify. Si quelqu'un modifie les tarifs dans l'admin sans
      resynchroniser la table, le drawer mentirait en silence. On interroge donc
      /cart/shipping_rates.json en arrière-plan et on corrige l'affichage s'il
      diverge. Le pire cas devient un affichage brièvement périmé, jamais un prix
      faux au moment de cliquer sur Checkout.

      L'appel sert aussi de source PRIMAIRE quand la devise du panier ne correspond
      pas à celle de la zone (Canada en CAD, Europe en EUR) : Liquid affiche alors
      "Calculated at checkout" et c'est cette réponse qui le remplace. L'API renvoie
      les tarifs dans la devise de présentation de la session, donc déjà convertis.

   Notes d'implémentation :
   - le script vit dans un asset et non dans cart.ajax.liquid : le drawer est injecté
     via insertAdjacentHTML, qui n'exécute pas les <script>. Un MutationObserver sur
     #CartContainer rattrape chaque re-rendu (le thème re-fetch le template entier à
     chaque changement de quantité, cf. getCartDrawerMarkup dans theme.js.liquid).
   - l'API renvoie les tarifs AVANT remise : la comparaison se fait donc sur le tarif
     brut, la règle du seuil est appliquée par-dessus, des deux côtés.
   - un appel par session et par (pays, palier de poids), mémorisé en sessionStorage.
     Aucun throttling observé sur 10 appels consécutifs (~500 ms chacun).
   - toute erreur réseau ou 422 est absorbée : on garde la valeur Liquid.
--------------------------------------------------------------------------- */
(function () {
  'use strict';

  /* 2026-10-09 : code devise après le prix au Canada et en Australie (« $42 CAD »), où « $ » seul
     se lit comme du dollar US. Lu sur Shopify.currency.active (posé par Shopify sur chaque page),
     pas sur une variable du layout. Ce fichier est chargé sur toutes les pages, après theme.min.js :
     on y enveloppe aussi les formateurs du thème (changement de variante, totaux du panier). */
  function pmSfx() {
    return ((window.Shopify && Shopify.currency && /^(CAD|AUD)$/.test(Shopify.currency.active)) ? '\u00a0' + Shopify.currency.active : '');
  }
  (function wrapThemeFormatters() {
    var sfx = pmSfx();
    if (!sfx) return;
    function wrap(o, k) {
      if (!o || typeof o[k] !== 'function' || o[k].__pmSfx) return;
      var f = o[k];
      o[k] = function () { var r = f.apply(this, arguments); return (typeof r === 'string' && r.indexOf(sfx) < 0) ? r + sfx : r; };
      o[k].__pmSfx = 1;
    }
    function go() { wrap(window.Shopify, 'formatMoney'); wrap(window.theme && window.theme.Currency, 'formatMoney'); }
    go();
    document.addEventListener('DOMContentLoaded', go);
    window.addEventListener('load', go);
  })();

  var FREE_MIN_USD = 99;

  /* Adresses sonde. L'endpoint exige pays + code postal (+ région pour US/CA),
     sinon il répond 422. Le tarif ne dépendant que du poids, n'importe quelle
     adresse valide du pays donne le bon montant — ces valeurs ne servent qu'à
     satisfaire la validation. Les pays absents ne sont pas sondés : la valeur
     Liquid tient, elle est simplement non vérifiée. La liste couvre tous les pays
     apparus dans les commandes des trois derniers mois. */
  var PROBES = {
    US: ['United States', 'New York', '10001'],
    CA: ['Canada', 'Ontario', 'M5V 2T6'],
    GB: ['United Kingdom', '', 'M1 1AE'],
    AU: ['Australia', 'New South Wales', '2000'],
    DE: ['Germany', '', '10115'],
    FR: ['France', '', '75001'],
    NL: ['Netherlands', '', '1011 AB'],
    SE: ['Sweden', '', '111 20'],
    IE: ['Ireland', '', 'D01 F5P2'],
    ES: ['Spain', '', '28001'],
    IT: ['Italy', '', '00100'],
    PL: ['Poland', '', '00-001'],
    CH: ['Switzerland', '', '8001'],
    FI: ['Finland', '', '00100'],
    BE: ['Belgium', '', '1000'],
    DK: ['Denmark', '', '1050'],
    NO: ['Norway', '', '0150'],
    AT: ['Austria', '', '1010'],
    PT: ['Portugal', '', '1000-001'],
    IS: ['Iceland', '', '101'],
    MX: ['Mexico', '', '06000']
  };

  function rate() {
    var c = window.Shopify && window.Shopify.currency;
    var r = c && parseFloat(c.rate);
    return r && isFinite(r) && r > 0 ? r : null;
  }

  /* Deux précautions, toutes deux payées par un bug observé en production.

     La locale est forcée à en-US au lieu d'être laissée à `undefined`. Avec undefined,
     Intl suit la langue du navigateur : sur un téléphone réglé en français ou en anglais
     britannique il désambiguïse le dollar et écrit « US$104.95 », là où tout le reste du
     drawer — qui passe par le filtre `money` de Shopify — affiche « $104.95 ». La boutique
     est monolingue anglaise ; en-US est donc aussi le bon choix pour les séparateurs.

     currencyDisplay narrowSymbol évite « CA$ » / « A$ » pour les marchés canadien et
     australien, où la cliente voit déjà sa devise partout ailleurs sous la forme « $ ».
     L'option est récente : si le navigateur la refuse, on retombe sur le symbole large,
     puis sur un formatage manuel. */
  function fmt(cents, ccy) {
    // 2026-10-09 : « $42 CAD » / « $42 AUD », comme le reste du site.
    return fmtBase(cents, ccy) + pmSfx();
  }
  function fmtBase(cents, ccy) {
    var opts = {
      style: 'currency',
      currency: ccy,
      minimumFractionDigits: cents % 100 === 0 ? 0 : 2
    };
    try {
      opts.currencyDisplay = 'narrowSymbol';
      return new Intl.NumberFormat('en-US', opts).format(cents / 100);
    } catch (e) {
      try {
        delete opts.currencyDisplay;
        return new Intl.NumberFormat('en-US', opts).format(cents / 100);
      } catch (e2) {
        return (cents / 100).toFixed(2) + ' ' + ccy;
      }
    }
  }

  /* Deux seuils, volontairement distincts.

     thresholdShown() sert au TEXTE ANNONCÉ dans la barre du haut : arrondi à l'unité
     supérieure, pour qu'une cliente qui atteint le montant affiché l'obtienne à coup sûr.

     thresholdReal() sert à la DÉCISION gratuit / payant de la ligne : valeur exacte,
     sans arrondi. Utiliser l'arrondi supérieur ici serait une faute — mesuré en préversion :
     un panier UK à £74.85 obtient bien la gratuité au checkout, mais un seuil arrondi à £75
     lui affichait « £10.15 » de livraison. Sur-annoncer un seuil est sans danger ; annoncer
     des frais qui ne seront pas prélevés décourage l'achat pour rien.

     99 × rate n'est pas une approximation : c'est le mécanisme même de Shopify, son minimum
     en devise boutique passé à son propre taux de marché. Reste une incertitude de quelques
     centimes aux abords immédiats du seuil, que rien ne permet de lever côté storefront. */
  function thresholdReal(ccy) {
    if (ccy === 'USD') return FREE_MIN_USD * 100;
    var r = rate();
    if (!r) return null;
    return Math.round(FREE_MIN_USD * r * 100);
  }

  /* Le tampon de 1 % avant arrondi n'est pas une précaution théorique. Le simple arrondi
     à l'unité ne garantit aucune marge : observé en préversion, 99 × 0.74710104 = £73.96
     donnait « £74 », soit 4 pence au-dessus du seuil estimé. Or le taux bouge en cours de
     journée — il est passé de 0.74762124 à 0.74710104 en quelques heures pendant ces tests.
     Le tampon assure au moins 1 % d'écart, largement au-dessus de cette dérive.
     En USD aucun tampon : le seuil vaut exactement 99, sans conversion. */
  function thresholdShown(ccy) {
    if (ccy === 'USD') return FREE_MIN_USD * 100;
    var r = rate();
    if (!r) return null;
    return Math.ceil(FREE_MIN_USD * r * 1.01) * 100;
  }

  function paint(row, baseCents, ccy, isFree) {
    var val = row.querySelector('[data-pm-shipval]');
    if (!val) return;
    if (isFree) {
      val.innerHTML = baseCents
        ? '<s class="pm-shiprow__was">' + fmt(baseCents, ccy) + '</s> ' +
          '<span class="pm-shiprow__free">FREE</span>'
        : '<span class="pm-shiprow__free">FREE</span>';
    } else if (baseCents) {
      val.innerHTML = '<span class="pm-shiprow__amt">' + fmt(baseCents, ccy) + '</span>';
    } else {
      val.innerHTML = '<span class="pm-shiprow__tbd">Calculated at checkout</span>';
    }
  }

  /* Order Total = sous-total + livraison.

     Écrit en JS et non en Liquid pour deux raisons. D'abord le format monétaire de la
     boutique est ${{amount_no_decimals}} : `{{ 8595 | money }}` afficherait « $86 ». Tous
     les prix produits sont des dollars entiers, le problème n'apparaissait pas avant qu'on
     additionne un port en .95. Ensuite Intl gère nativement toutes les devises de
     présentation, y compris celles où le montant ne vient pas de Liquid mais de la sonde
     (Canada, Europe).

     Sans danger ici : applyDiscount et destroyDiscount (theme.js.liquid) réécrivent bien
     [data-cart-total], mais sortent toutes deux sur `if (theme.settings.dbtfyCartSavings)
     return;` — et ce réglage vaut true sur cette boutique.

     Le montant est toujours recalculé depuis data-pm-total, jamais depuis le texte affiché :
     un re-rendu du drawer ne peut pas additionner le port deux fois. */
  function applyTotal(row, shipCents, isFree) {
    if (shipCents === null || shipCents === undefined) return;
    var block = document.querySelector('[data-cart-total]');
    if (!block) return;
    var total = parseInt(row.getAttribute('data-pm-total'), 10);
    if (!isFinite(total)) return;

    var ccy = row.getAttribute('data-pm-ccy') || 'USD';

    /* 2026-09-10 : le port n'est plus ajoute au total, a la demande d'Imran. « Order Total »
       vaut donc desormais le prix des articles seul, comme cart.total_price.

       On garde malgre tout ce passage : il reecrit le montant depuis les centimes exacts,
       la ou le filtre `money` de la boutique est sans decimales et afficherait $58.50 en
       « $59 ». Le retirer ferait reapparaitre cet arrondi.

       add reste dans la formule plutot que d'etre supprime : remettre la ligne d'origine
       (isFree ? 0 : shipCents) suffit a retablir le total port compris. */
    var add = 0;

    /* Le module "cart savings" insère à côté du total un prix barré (valeur catalogue
       avant remise) et affiche "You Save $X" juste à gauche. Si le total intègre le port
       mais pas le prix barré, les trois chiffres cessent de s'accorder : $117 → $67.95
       ne fait pas $59 d'économie. On ajoute donc le port des deux côtés — l'économie
       affichée reste exactement celle des articles, et l'addition retombe juste. */
    var strike = block.querySelector('.text-strike .money');
    var slot = block.querySelector('.money');
    if (slot === strike) slot = null;
    if (slot) slot.textContent = fmt(total + add, ccy);

    var compare = parseInt(row.getAttribute('data-pm-compare'), 10);
    if (strike && isFinite(compare) && compare > 0) {
      strike.textContent = fmt(compare + add, ccy);
    }
  }

  /* Le module "cart savings" réécrit ce bloc au même moment que nous, sans ordre garanti.
     applyTotal étant idempotent — toujours recalculé depuis data-pm-total, jamais depuis
     l'affichage — on le rejoue deux fois pour gagner la course quel qu'en soit l'ordre. */
  function applyTotalSticky(row, shipCents, isFree) {
    applyTotal(row, shipCents, isFree);
    setTimeout(function () { applyTotal(row, shipCents, isFree); }, 200);
    setTimeout(function () { applyTotal(row, shipCents, isFree); }, 700);
  }

  /* Tarif brut le moins cher pour l'adresse sonde, en centimes. null si indisponible. */
  function fetchRate(country, weight) {
    var probe = PROBES[country];
    if (!probe) return Promise.resolve(null);

    var key = 'pmship:' + country + ':' + weight;
    try {
      var cached = sessionStorage.getItem(key);
      if (cached !== null) return Promise.resolve(cached === '' ? null : parseInt(cached, 10));
    } catch (e) { /* sessionStorage indisponible : on sonde sans cache */ }

    var q = 'shipping_address[country]=' + encodeURIComponent(probe[0]) +
            '&shipping_address[zip]=' + encodeURIComponent(probe[2]);
    if (probe[1]) q += '&shipping_address[province]=' + encodeURIComponent(probe[1]);

    return fetch('/cart/shipping_rates.json?' + q, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var rates = j && j.shipping_rates;
        if (!rates || !rates.length) return null;
        var min = null;
        for (var i = 0; i < rates.length; i++) {
          var c = Math.round(parseFloat(rates[i].price) * 100);
          if (isFinite(c) && (min === null || c < min)) min = c;
        }
        return min;
      })
      .catch(function () { return null; })
      .then(function (v) {
        try { sessionStorage.setItem(key, v === null ? '' : String(v)); } catch (e) {}
        return v;
      });
  }

  function sync() {
    var row = document.querySelector('[data-pm-shiprow]');
    if (!row || row.hasAttribute('data-pm-synced')) return;
    row.setAttribute('data-pm-synced', '1');

    var ccy = row.getAttribute('data-pm-ccy') || 'USD';
    var country = row.getAttribute('data-pm-country') || '';
    var weight = parseInt(row.getAttribute('data-pm-weight'), 10) || 0;
    var total = parseInt(row.getAttribute('data-pm-total'), 10) || 0;
    var baseAttr = row.getAttribute('data-pm-base');
    var base = baseAttr ? parseInt(baseAttr, 10) : null;

    var thrReal = thresholdReal(ccy);
    var isFree = thrReal !== null && total >= thrReal;

    /* Bandeau vert de confirmation. Liquid ne peut le rendre visible qu'en USD (pm_ship_done),
       le seuil etant exprime en dollars ; ici on dispose du seuil converti, donc de l'etat
       reel quelle que soit la devise. */
    var freebar = document.querySelector('[data-pm-freebar]');
    if (freebar) {
      if (isFree) freebar.removeAttribute('hidden');
      else freebar.setAttribute('hidden', '');
    }

    /* Barre du haut : renseigner le seuil converti puis la révéler. Hors USD elle
       part masquée pour ne pas laisser clignoter "$99" devant une cliente en euros. */
    var bar = document.querySelector('[data-pm-shipbar]');
    var thrShown = thresholdShown(ccy);
    if (bar && bar.hasAttribute('hidden') && thrShown !== null) {
      var slot = bar.querySelector('[data-pm-thresh]');
      if (slot) slot.textContent = fmt(thrShown, ccy);
      bar.removeAttribute('hidden');
    }

    /* 2026-09-25 : bloc B1 du pied de panier, « You're $X away from Free Shipping ».
       Le montant restant part du seuil AFFICHÉ (arrondi vers le haut hors USD) : annoncer
       un reste un peu trop grand est sans danger, l'inverse promettrait une gratuité que
       le checkout refuserait. L'état gratuit / payant, lui, suit isFree (seuil exact),
       comme la ligne Shipping. */
    var nudge = document.querySelector('[data-pm-nudge]');
    if (nudge && thrShown !== null) {
      if (isFree) nudge.classList.add('is-free');
      else nudge.classList.remove('is-free');
      var left = nudge.querySelector('[data-pm-left]');
      if (left && !isFree) left.textContent = fmt(Math.max(thrShown - total, 0), ccy);
      var fill = nudge.querySelector('[data-pm-fill]');
      if (fill) {
        var pct = isFree ? 100 : Math.max(3, Math.min(100, total / thrShown * 100));
        fill.style.width = pct.toFixed(1) + '%';
      }
      nudge.removeAttribute('hidden');
    }

    /* Liquid n'a pas pu calculer le seuil hors USD : rectifier l'état affiché. */
    if (ccy !== 'USD' && isFree) paint(row, base, ccy, true);

    /* Ligne Discount : Liquid l'a déjà peinte, mais via le filtre `money` de la boutique,
       qui est sans décimales — une économie de $59.50 s'afficherait « $60 ». On la réécrit
       depuis les centimes exacts, ce qui règle aussi les devises non-USD. */
    var disc = document.querySelector('[data-pm-discval]');
    var orig = document.querySelector('[data-pm-origval]');
    var compare = parseInt(row.getAttribute('data-pm-compare'), 10);
    if (isFinite(compare) && compare > total) {
      if (disc) disc.innerHTML = '<span class="pm-shiprow__save">−' + fmt(compare - total, ccy) + '</span>';
      if (orig) orig.textContent = fmt(compare, ccy);
    }

    /* Final Price : le sous-total après remise, hors livraison. Reformaté ici pour la
       même raison que les autres montants — le filtre `money` de la boutique est sans
       décimales et afficherait $58.50 comme « $59 ». */
    var net = document.querySelector('[data-pm-netval]');
    if (net) net.textContent = fmt(total, ccy);

    applyTotalSticky(row, base, isFree);

    /* Contrôle en arrière-plan — et source primaire quand base est absente.

       Tolérance hors USD : l'endpoint calcule en devise boutique puis reconvertit, là où
       le checkout applique directement le tarif natif de la zone. Mesuré en préversion sur
       le marché UK : profil et checkout disent £9.95, l'API répond £10.15 — 2 % d'écart de
       pur aller-retour de change. Sans garde-fou, l'auto-contrôle « corrigerait » une valeur
       juste en valeur fausse. On n'agit donc hors USD qu'au-delà de 5 %, ce qui laisse passer
       le bruit de change mais rattrape un vrai changement de tarif. En USD aucune conversion
       n'intervient : la moindre différence est réelle et corrigée. */
    fetchRate(country, weight).then(function (live) {
      if (live === null) return;                   // sonde indisponible : la table tient
      if (base === null) {                         // seule source connue
        paint(row, live, ccy, isFree);
        applyTotalSticky(row, live, isFree);
        return;
      }
      if (live === base) return;                   // table conforme au profil réel
      if (ccy !== 'USD' && Math.abs(live - base) <= base * 0.05) return;
      paint(row, live, ccy, isFree);
      applyTotalSticky(row, live, isFree);
    });
  }

  function observe() {
    var target = document.getElementById('CartContainer');
    if (!target) return;
    new MutationObserver(function () { sync(); }).observe(target, { childList: true, subtree: true });
    sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  } else {
    observe();
  }
})();
